import { useState, type FormEvent } from 'react';
import { useTestConnector } from '@/lib/api/connectors';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface ConnectorTestPanelProps {
  connectorId: string;
  agentParameters: { name: string; required: boolean }[];
}

/** Manual "test request" action: run the connector with caller-supplied values. */
export function ConnectorTestPanel({ connectorId, agentParameters }: ConnectorTestPanelProps) {
  const testConnector = useTestConnector(connectorId);
  const [values, setValues] = useState<Record<string, string>>({});

  function handleRun(e: FormEvent) {
    e.preventDefault();
    testConnector.mutate({ parameters: values });
  }

  return (
    <Card title="Test request" description="Runs the configured request with the values below.">
      <form onSubmit={handleRun} className="space-y-3">
        {agentParameters.length === 0 && (
          <p className="text-xs text-gray-500">This connector has no agent-supplied parameters.</p>
        )}
        {agentParameters.map((p) => (
          <Input
            key={p.name}
            label={p.name}
            value={values[p.name] ?? ''}
            onChange={(e) => setValues((prev) => ({ ...prev, [p.name]: e.target.value }))}
            required={p.required}
            data-testid={`test-param-${p.name}`}
          />
        ))}
        <Button type="submit" disabled={testConnector.isPending} data-testid="run-test-button">
          {testConnector.isPending ? 'Running...' : 'Run test'}
        </Button>
      </form>

      {testConnector.error && (
        <p
          role="alert"
          className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          data-testid="test-error"
        >
          {(testConnector.error as Error).message}
        </p>
      )}

      {testConnector.data && (
        <div className="mt-3 space-y-2 rounded-md bg-gray-50 p-3 text-sm" data-testid="test-result">
          <p>
            Status: <span className="font-mono">{testConnector.data.status_code}</span>
            {' - '}
            {testConnector.data.latency_ms} ms
          </p>
          {testConnector.data.truncated && (
            <p className="text-xs text-amber-700">Response truncated.</p>
          )}
          <pre
            className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-white p-2 text-xs"
            data-testid="test-result-body"
          >
            {testConnector.data.body}
          </pre>
        </div>
      )}
    </Card>
  );
}
